import { graphql } from "@/config/graphql";
import { safeNumber } from "@/utils/number";
import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { SupplySchema, supplySchema } from "../SupplyReportForm.schema";
import { useEffect } from "react";

const Content = styled(Box)`
  padding: 16px 24px;
`;
const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const getSupplyResourcesSuggestionsQuery = graphql(`
  query getSupplyResourcesSuggestions($params: SuggestedSupplyDetailsParams!) {
    suggestedSupplyDetails(params: $params) {
      resourceName
      initialQuantity
    }
  }
`);

const addOtherSupplyDetailMutation = graphql(`
  mutation addOtherSupplyDetail($params: AddSupplyDetailParams!) {
    addSupplyDetail(params: $params) {
      report {
        id
        supplyDetails {
          id
        }
      }
    }
  }
`);

interface OtherSuppliesReportFormProps {
  close: () => void;
}
export const OtherSuppliesReportForm = (
  props: OtherSuppliesReportFormProps
) => {
  const params = useParams();
  const { control, handleSubmit, watch, setValue } = useForm<SupplySchema>({
    resolver: zodResolver(supplySchema),
    defaultValues: {
      resourceName: "",
    },
  });
  const { data } = useQuery(getSupplyResourcesSuggestionsQuery, {
    variables: { params: { reportId: +params.id! } },
    fetchPolicy: "cache-and-network",
  });
  const [addOtherSupplyDetail, { loading }] = useMutation(
    addOtherSupplyDetailMutation,
    {
      refetchQueries: [getSupplyResourcesSuggestionsQuery],
    }
  );

  const handleClose = () => {
    props.close();
  };
  const onSubmit = async (data: SupplySchema) => {
    await addOtherSupplyDetail({
      variables: {
        params: {
          ...data,
          reportId: +params.id!,
        },
      },
    });
    handleClose();
  };

  const [initialQuantity, finalQuantity, incomeQuantity, resourceName] = watch([
    "initialQuantity",
    "finalQuantity",
    "incomeQuantity",
    "resourceName",
  ]);
  const nameOptions = Array.from(
    new Set(
      data?.suggestedSupplyDetails.map((detail) => detail.resourceName) || []
    )
  );
  useEffect(() => {
    const selectedOption = data?.suggestedSupplyDetails.find(
      (option) => option.resourceName === resourceName
    );
    if (!selectedOption?.initialQuantity) return;
    setValue("initialQuantity", selectedOption.initialQuantity);
  }, [data?.suggestedSupplyDetails, resourceName, setValue]);
  const consumedQuantity =
    safeNumber(initialQuantity) -
    safeNumber(finalQuantity) +
    safeNumber(incomeQuantity);
  return (
    <>
      <Content>
        <Typography variant="h6">Agregar Otro</Typography>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="resourceName"
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={nameOptions}
                onChange={(_, value) => {
                  field.onChange(value);
                }}
                freeSolo
                noOptionsText="No hay opciones"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    label="Suministro"
                    sx={{ maxWidth: "70%" }}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              ></Autocomplete>
            )}
          />
          <Controller
            name="initialQuantity"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Saldo inicial (+)"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ton</InputAdornment>
                  ),
                }}
                fullWidth
                type="number"
                error={!!fieldState.error}
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                helperText={fieldState.error?.message}
                {...field}
              ></TextField>
            )}
          />
          <Controller
            name="incomeQuantity"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Ingresos (+)"
                fullWidth
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ton</InputAdornment>
                  ),
                }}
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                {...field}
              ></TextField>
            )}
          />
          <Controller
            name="finalQuantity"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Saldo final (-)"
                fullWidth
                type="number"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ton</InputAdornment>
                  ),
                }}
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                {...field}
              ></TextField>
            )}
          />
          <Controller
            name="comment"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Typography
                  fontWeight={800}
                  color={fieldState.error ? "error" : "inherit"}
                >
                  Consumo:{" "}
                  <Typography component="span" display={"inline"}>
                    {consumedQuantity.toLocaleString("es-Cl")} ton
                  </Typography>
                </Typography>
                <TextField
                  label="Comentario"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...field}
                  value={field.value ?? ""}
                ></TextField>
              </>
            )}
          />
        </Form>
      </Content>
      <DialogActions sx={{ padding: "8px 24px" }}>
        <Button onClick={handleClose} color="error">
          CANCELAR
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="info"
          disabled={loading}
        >
          GUARDAR{" "}
          {loading && <CircularProgress sx={{ marginLeft: 1 }} size={20} />}
        </Button>
      </DialogActions>
    </>
  );
};
